<template>
  <div class="empty-layout">
    <!-- 背景装饰 -->
    <div class="background-decorations">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
      <div class="circle circle-4"></div>
    </div>

    <!-- 头部（仅显示Logo） -->
    <div class="layout-header" v-if="showHeader">
      <div class="logo-container">
        <div class="logo" @click="goHome">
          <el-icon class="logo-icon"><Ticket /></el-icon>
          <span class="logo-text">工单系统</span>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <main class="layout-main">
      <div class="main-container">
        <!-- 页面切换动画 -->
        <transition name="fade" mode="out-in">
          <router-view v-slot="{ Component }">
            <component :is="Component" />
          </router-view>
        </transition>
      </div>
    </main>

    <!-- 底部信息 -->
    <footer class="layout-footer" v-if="showFooter">
      <div class="footer-content">
        <div class="copyright">
          © {{ currentYear }} 工单管理系统. All Rights Reserved.
        </div>
        <div class="links">
          <a href="#" @click.prevent="goToHelp">帮助中心</a>
          <span class="divider">|</span>
          <a href="#" @click.prevent="goToPrivacy">隐私政策</a>
          <span class="divider">|</span>
          <a href="#" @click.prevent="goToTerms">服务条款</a>
        </div>
        <div class="version">版本: {{ version }}</div>
      </div>
    </footer>

    <!-- 语言切换 -->
    <div class="language-switcher">
      <el-dropdown @command="changeLanguage" trigger="click">
        <span class="language-trigger">
          <el-icon><Connection /></el-icon>
          <span>{{ currentLanguage }}</span>
          <el-icon><ArrowDown /></el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="zh-CN">简体中文</el-dropdown-item>
            <el-dropdown-item command="en-US">English</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <!-- 主题切换 -->
    <div class="theme-switcher">
      <el-tooltip :content="themeTip" placement="left">
        <el-button
          circle
          :icon="themeIcon"
          @click="toggleTheme"
          class="theme-button"
        />
      </el-tooltip>
    </div>

    <!-- 全局加载状态 -->
    <div class="global-loading" v-if="globalLoading">
      <div class="loading-content">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <span class="loading-text">加载中...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { ElMessage } from 'element-plus'
import {
  Ticket,
  Connection,
  ArrowDown,
  Loading,
  Sunny,
  Moon
} from '@element-plus/icons-vue'

const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const authStore = useAuthStore()
const appStore = useAppStore()

// 响应式数据
const currentYear = ref(new Date().getFullYear())
const currentTheme = ref('light')
const showHeader = ref(true)
const showFooter = ref(true)
const version = ref('v1.0.0')
const globalLoading = ref(false)

// 计算属性
const currentLanguage = computed(() => {
  return locale.value === 'zh-CN' ? '中文' : 'English'
})

const themeIcon = computed(() => {
  return currentTheme.value === 'light' ? Moon : Sunny
})

const themeTip = computed(() => {
  return currentTheme.value === 'light' ? '切换到深色模式' : '切换到浅色模式'
})

// 方法
const goHome = () => {
  if (authStore.isAuthenticated) {
    router.push('/dashboard')
  } else {
    router.push('/auth/login')
  }
}

const goToHelp = () => {
  ElMessage.info('帮助中心功能开发中')
}

const goToPrivacy = () => {
  ElMessage.info('隐私政策功能开发中')
}

const goToTerms = () => {
  ElMessage.info('服务条款功能开发中')
}

const changeLanguage = (lang) => {
  locale.value = lang
  localStorage.setItem('language', lang)
  ElMessage.success(`语言已切换为 ${lang === 'zh-CN' ? '简体中文' : 'English'}`)
}

const toggleTheme = () => {
  currentTheme.value = currentTheme.value === 'light' ? 'dark' : 'light'
  localStorage.setItem('theme', currentTheme.value)
  applyTheme(currentTheme.value)
}

const applyTheme = (theme) => {
  const html = document.documentElement
  html.classList.toggle('dark', theme === 'dark')
}

const initTheme = () => {
  const savedTheme = localStorage.getItem('theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  if (savedTheme) {
    currentTheme.value = savedTheme
  } else if (prefersDark) {
    currentTheme.value = 'dark'
  } else {
    currentTheme.value = 'light'
  }

  applyTheme(currentTheme.value)
}

const initLanguage = () => {
  const savedLanguage = localStorage.getItem('language')
  if (savedLanguage) {
    locale.value = savedLanguage
  }
}

const handleResize = () => {
  // 在小屏幕上隐藏一些元素
  const width = window.innerWidth
  if (width < 768) {
    showFooter.value = false
  } else {
    showFooter.value = true
  }
}

// 模拟全局加载
const simulateLoading = () => {
  globalLoading.value = true
  setTimeout(() => {
    globalLoading.value = false
  }, 1000)
}

// 监听路由变化
watch(() => route.path, (newPath) => {
  // 在一些特定页面隐藏头部
  if (newPath.includes('/auth/reset-password')) {
    showHeader.value = false
  } else {
    showHeader.value = true
  }
})

// 生命周期
onMounted(() => {
  initTheme()
  initLanguage()
  handleResize()
  window.addEventListener('resize', handleResize)

  // 页面加载完成后显示内容
  setTimeout(() => {
    document.body.classList.add('loaded')
  }, 300)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped lang="scss">
.empty-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  .background-decorations {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
    overflow: hidden;

    .circle {
      position: absolute;
      border-radius: 50%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      opacity: 0.1;
      filter: blur(60px);

      &.circle-1 {
        width: 300px;
        height: 300px;
        top: -100px;
        left: -100px;
      }

      &.circle-2 {
        width: 400px;
        height: 400px;
        bottom: -200px;
        right: -100px;
      }

      &.circle-3 {
        width: 200px;
        height: 200px;
        top: 30%;
        left: 70%;
      }

      &.circle-4 {
        width: 250px;
        height: 250px;
        bottom: 40%;
        left: 10%;
      }
    }
  }

  .layout-header {
    padding: 20px 40px;
    background: transparent;
    position: relative;
    z-index: 10;

    @media (max-width: 768px) {
      padding: 15px 20px;
    }

    .logo-container {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .logo {
        display: flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        transition: opacity 0.3s;

        &:hover {
          opacity: 0.8;
        }

        .logo-icon {
          font-size: 32px;
          color: #667eea;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .logo-text {
          font-size: 24px;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;

          @media (max-width: 768px) {
            font-size: 20px;
          }
        }
      }
    }
  }

  .layout-main {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    position: relative;
    z-index: 1;

    .main-container {
      width: 100%;
      max-width: 1200px;
      margin: 0 auto;
    }
  }

  .layout-footer {
    padding: 20px 40px;
    background: transparent;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    position: relative;
    z-index: 10;

    .footer-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #666;
      font-size: 14px;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
        text-align: center;
      }

      .copyright {
        flex: 1;
      }

      .links {
        flex: 2;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 15px;

        a {
          color: #667eea;
          text-decoration: none;
          transition: color 0.3s;

          &:hover {
            color: #764ba2;
            text-decoration: underline;
          }
        }

        .divider {
          color: #999;
          user-select: none;
        }
      }

      .version {
        flex: 1;
        text-align: right;
        color: #999;
      }
    }
  }

  .language-switcher {
    position: fixed;
    top: 30px;
    right: 30px;
    z-index: 100;

    .language-trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      background: white;
      border-radius: 20px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      cursor: pointer;
      transition: all 0.3s;
      color: #333;
      font-weight: 500;

      &:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
      }

      .el-icon {
        color: #667eea;
      }
    }
  }

  .theme-switcher {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 100;

    .theme-button {
      width: 50px;
      height: 50px;
      font-size: 20px;
      background: white;
      border: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transition: all 0.3s;

      &:hover {
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
      }

      :deep(.el-icon) {
        color: #667eea;
      }
    }
  }

  .global-loading {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(5px);

    .loading-content {
      text-align: center;
      animation: fadeIn 0.3s ease;

      .loading-icon {
        font-size: 48px;
        color: #667eea;
        animation: rotating 2s linear infinite;
        display: block;
        margin-bottom: 16px;
      }

      .loading-text {
        font-size: 16px;
        color: #333;
        font-weight: 500;
      }
    }
  }
}

// 深色主题样式
:deep(.dark) {
  .empty-layout {
    .background-decorations .circle {
      background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
      opacity: 0.2;
    }

    .layout-footer {
      border-top: 1px solid rgba(255, 255, 255, 0.1);

      .footer-content {
        color: #ccc;

        .version {
          color: #aaa;
        }
      }
    }

    .language-switcher .language-trigger {
      background: #2d3748;
      color: #e2e8f0;
    }

    .theme-switcher .theme-button {
      background: #2d3748;

      :deep(.el-icon) {
        color: #e2e8f0;
      }
    }

    .global-loading {
      background: rgba(0, 0, 0, 0.8);

      .loading-text {
        color: #fff;
      }
    }
  }
}

// 动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 响应式
@media (max-width: 768px) {
  .empty-layout {
    .layout-header {
      padding: 15px 20px;
    }

    .layout-main {
      padding: 20px 15px;
    }

    .layout-footer {
      padding: 15px 20px;

      .footer-content {
        .copyright,
        .version {
          font-size: 12px;
        }

        .links {
          gap: 10px;
          font-size: 12px;
        }
      }
    }

    .language-switcher,
    .theme-switcher {
      position: absolute;

      .language-trigger {
        padding: 6px 12px;
        font-size: 14px;
      }

      .theme-button {
        width: 40px;
        height: 40px;
        font-size: 16px;
      }
    }
  }
}

@media (max-width: 480px) {
  .empty-layout {
    .layout-header .logo-container .logo .logo-text {
      font-size: 18px;
    }

    .language-switcher,
    .theme-switcher {
      display: none;
    }
  }
}
</style>